<template>
  <!-- eslint-disable vue/no-multiple-template-root -->
  <div data-layout-name="default">
    <div id="wrapper-header">
      <div
        :class="{ visible: shouldDisplayBreakingNewsBanner }"
        id="wrapper-banner"
      >
        <BreakingNewsBanner data-allow-mismatch />
      </div>

      <TheHeader />
    </div>

    <main>
      <slot />
    </main>

    <TheFooter />
  </div>

  <ClientOnly>
    <Teleport defer to="#overlay">
      <transition name="fade" mode="out-in">
        <TheSearch v-if="isSearchVisible" @close="closeSearch" />
      </transition>
    </Teleport>

    <transition name="slide-from-right">
      <Menu.Mobile v-if="isMobileMenuVisible" />
    </transition>
  </ClientOnly>
</template>

<script setup lang="ts">
// components
import TheHeader from '~/components/TheHeader/TheHeader.vue';
import TheSearch from '~/components/redesign/TheSearch';
import BreakingNewsBanner from '~/components/redesign/BreakingNewsBanner.vue';
import TheFooter from '~/components/redesign/TheFooter.vue';
import Menu from '~/components/Menu';

// composables
import { storeToRefs } from 'pinia';
import onRouteChange from '~/composables/onRouteChange';
import { useHeaderOffset } from '~/composables/useHeaderOffset';

// store
import useUIStore from '~/stores/ui';

const uiStore = useUIStore();
const { setOverlay, setMobileMenuVisibility, setSearchVisibility } = uiStore;
const {
  isMobileMenuVisible,
  isSearchVisible,
  shouldDisplayBreakingNewsBanner,
} = storeToRefs(uiStore);

const closeMobileMenu = () => {
  setOverlay({ visible: false });
  setMobileMenuVisibility(false);
};

const closeSearch = () => {
  setOverlay({ visible: false });
  setSearchVisibility(false);
};

if (import.meta.client) {
  const { headerOffset } = useHeaderOffset();

  useHead({
    htmlAttrs: {
      style: computed(() =>
        headerOffset.value > 0
          ? `--scroll-offset: ${headerOffset.value}px;`
          : '',
      ),
    },
  });
}

onRouteChange(closeMobileMenu);
</script>

<style lang="scss">
@use '_/mixins/flex';
@use '_/mixins/media';
@use '_/mixins/size';
@use '_/mixins/position';
@use '_/transitions';
@use '_/fn';

[data-layout-name='default'] {
  @include flex.stretch-stretch;

  flex-direction: column;
  min-height: 100vh;

  main {
    flex: 1;
  }

  h1[id],
  h2[id],
  h3[id] {
    scroll-margin-top: calc(
      var(--scroll-offset, var(--default-header-height)) + 0.5rem
    );
  }

  section[id] {
    scroll-margin-top: var(--scroll-offset, var(--default-header-height));
  }

  #wrapper-header {
    position: sticky;
    top: 0;
    z-index: 7;

    #wrapper-banner {
      overflow: hidden;
      transition: all 0.5s ease;
      max-height: 0;

      [data-component-name='BreakingNewsBanner'] {
        transform: translateY(-100%);
        transition: all 0.4s ease;
      }

      &.visible {
        max-height: 5rem;

        @include media.tablet {
          max-height: 6.25rem;
        }

        @include media.mobile {
          max-height: 8rem;
        }

        [data-component-name='BreakingNewsBanner'] {
          transform: translateY(0);
        }
      }
    }
  }
}

[data-component-name='TheSearch'] {
  position: absolute;
  top: 5.5rem;

  @include size.fluid-max-width(75rem);

  max-height: calc(100dvh - (5.5rem + 1.5rem));

  @include media.tablet {
    @include size.fluid-max-width(calc(100% - 4rem));
  }

  @include media.mobile {
    top: 3.5rem;

    @include size.fluid-max-width(100%);

    max-height: calc(100dvh - (3.5rem + 1rem));
  }
}

[data-component-name='Menu.Mobile'] {
  @include position.fixed-full;
  top: var(--scroll-offset, var(--default-header-height));
  z-index: 6;
  transition: top 0.5s ease;
}
</style>
